import { Container, InputGroup } from 'rsuite';
import { useCustomTheme } from '../../hooks/useCustomTheme';
import { ContentHeader } from '../../components/ContentHeader';
import DuvidasButton from '../../components/DuvidasButton';
import { CustomInput } from '../../components/CustomInput';
import SearchIcon from '@rsuite/icons/Search';
import PlusIcon from '@rsuite/icons/Plus';
import { CustomButton } from '../../components/CustomButton';
import { useAuth } from '../../hooks/auth';
import { debounce } from '../../components/funcoes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { toastInfo } from '../../toasts';
import EditarCadastrarFeriado from './EditarCadastrar';
import { FaCalendarDay, FaClock, FaInfoCircle, FaEllipsisV } from 'react-icons/fa';
import {
  CardGrid,
  Card,
  CardHeader,
  CardTitle,
  CardSubtitle,
  HeaderActions,
  StatusBadge,
  ItemList,
  ItemRow,
  ItemText,
  Hint,
  EmptyState,
  LoadingState,
  CardMenu,
} from '../../components/ManagementCardGrid';

const mesesDoAno = [
  { label: 'Janeiro', value: 1 },
  { label: 'Fevereiro', value: 2 },
  { label: 'Março', value: 3 },
  { label: 'Abril', value: 4 },
  { label: 'Maio', value: 5 },
  { label: 'Junho', value: 6 },
  { label: 'Julho', value: 7 },
  { label: 'Agosto', value: 8 },
  { label: 'Setembro', value: 9 },
  { label: 'Outubro', value: 10 },
  { label: 'Novembro', value: 11 },
  { label: 'Dezembro', value: 12 },
];

const formatarHora = (hora = '') => (hora?.length > 5 ? hora.slice(0, 5) : hora);

const Feriado = () => {
  const { id_empresa } = useAuth();
  const { theme } = useCustomTheme();
  const [loading, setLoading] = useState(true);
  const [buscar, setBuscar] = useState('');
  const [feriado, setFeriado] = useState(null);
  const [feriados, setFeriados] = useState([]);
  const [open, setOpen] = useState(false);

  const feriadosMemo = useMemo(
    () =>
      buscar.trim() === ''
        ? feriados
        : feriados.filter(({ Descricao = '' }) =>
            Descricao?.toLocaleLowerCase().includes(buscar),
          ),
    [buscar, feriados],
  );

  const debounceSearch = debounce((valor = '') => {
    setBuscar(valor.toLocaleLowerCase());
  }, 1000);

  const abrirDados = (feriado) => {
    setFeriado(feriado);
    setOpen(true);
  };

  const cadastrar = useCallback(() => {
    setFeriado(null);
    setOpen(true);
  }, []);

  const alterarStatus = async (feriado, status) => {
    await api
      .patch(`feriado/${feriado.Id}`, {
        Ativo: status,
      })
      .then((res) => {
        toastInfo('Feriado alterado');
        setFeriados((state) =>
          state.map((e) => (e.Id !== res.data.Id ? e : res.data)),
        );
      });
  };

  const atualizar = async () => {
    await api
      .get(`feriado/listarGestor/${id_empresa}`)
      .then((res) => {
        setFeriados(res.data);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    atualizar();
  }, []);

  return (
    <Container
      style={{
        padding: '10px',
        height: 'calc(100vh - 60px)',
        backgroundColor: theme.colors.background,
        overflowY: 'auto',
        maxWidth: 1600,
        margin: '0 auto',
      }}
    >
      <ContentHeader
        title="Feriados e Pausas"
        subtitle="Gerencie os feriados e pausas da sua empresa"
        icon={FaCalendarDay}
        secondComponent={
          <InputGroup
            inside
            style={{
              width: '36vw',
              background: theme.colors.background,
              border: 'none',
            }}
          >
            <CustomInput onChange={debounceSearch} />
            <InputGroup.Addon>
              <SearchIcon color={theme.colors.textSecondary} />
            </InputGroup.Addon>
          </InputGroup>
        }
        thirdComponent={
          <>
            <DuvidasButton tela="feriado" />
            <CustomButton appearance="primary" onClick={cadastrar}>
              <PlusIcon style={{ marginRight: 6 }} /> Adicionar
            </CustomButton>
          </>
        }
      />

      {loading ? (
        <LoadingState>Carregando...</LoadingState>
      ) : feriadosMemo?.length === 0 ? (
        <EmptyState>Nenhum feriado ou pausa cadastrado</EmptyState>
      ) : (
        <CardGrid minWidth="240px">
          {feriadosMemo.map((item) => {
            const mes = mesesDoAno.find((m) => m.value === item.Mes);
            const diaInteiro =
              (item.Inicio || '00:00:00') === '00:00:00' &&
              (item.Fim || '00:00:00') === '00:00:00';

            return (
              <Card key={item.Id} $clickable={false}>
                <CardHeader>
                  <div>
                    <CardTitle>{item.Descricao}</CardTitle>
                    <CardSubtitle>
                      {item.Dia} de {mes?.label || item.Mes}
                    </CardSubtitle>
                  </div>
                  <HeaderActions>
                    <StatusBadge
                      $active={!!item.Ativo}
                      onClick={() => alterarStatus(item, item.Ativo ? 0 : 1)}
                      title={item.Ativo ? 'Clique para desativar' : 'Clique para ativar'}
                    >
                      {item.Ativo ? 'Ativo' : 'Inativo'}
                    </StatusBadge>
                    <CardMenu
                      options={[
                        { label: 'Editar', onClick: () => abrirDados(item) },
                        {
                          label: item.Ativo ? 'Desativar' : 'Ativar',
                          onClick: () => alterarStatus(item, item.Ativo ? 0 : 1),
                        },
                      ]}
                    />
                  </HeaderActions>
                </CardHeader>

                <ItemList>
                  <ItemRow>
                    <ItemText $muted={!item.Ativo} $strike={!item.Ativo}>
                      <FaClock size={11} />
                      {diaInteiro
                        ? 'Dia inteiro'
                        : `${formatarHora(item.Inicio)} - ${formatarHora(item.Fim)}`}
                    </ItemText>
                  </ItemRow>
                </ItemList>
              </Card>
            );
          })}
        </CardGrid>
      )}

      <Hint >
        <FaInfoCircle />
        <span>
          Clique em "Adicionar" para cadastrar um novo feriado ou pausa, ou no{' '}
          <FaEllipsisV size={10} style={{ margin: '0 2px' }} /> de um item
          existente para editá-lo ou ativar/desativar.
        </span>
      </Hint>

      {open && (
        <EditarCadastrarFeriado
          mesesDoAno={mesesDoAno}
          modal={open}
          close={() => setOpen(false)}
          feriado={feriado}
          setFeriados={setFeriados}
          id_empresa={id_empresa}
        />
      )}
    </Container>
  );
};

export default Feriado;
